import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ITENS_NAV, URL_APP } from '../dados/navegacao';
import { NavLinks } from './nav-links';

type MenuMobileProps = {
  aberto: boolean;
  ativo: string | null;
  onFechar: () => void;
};

/**
 * Drawer de navegação mobile, aberto pelo botão de hambúrguer do header.
 *
 * ## Por que um PORTAL, e não uma `<div>` no lugar onde ele é escrito
 * Ele mora dentro do `<header>`, que ganha `backdrop-blur` depois de 24px de
 * rolagem. E `backdrop-filter` transforma o elemento em BLOCO CONTENTOR dos
 * descendentes `fixed`: `fixed inset-0` deixava de valer contra a viewport e
 * passava a valer contra a caixa do header.
 *
 * Medido num iPhone: no topo a gaveta tinha 746px de altura, a tela inteira;
 * depois de rolar, 76px — uma tira escondida atrás do próprio header. Para
 * quem usa, "o menu não abre". E só acontecia ROLADO, que é justamente o
 * estado em que alguém procura o menu.
 *
 * Nenhum `z-index` resolveria: o problema não era ordem de pintura, era
 * contra QUEM as coordenadas eram calculadas. O portal prende a gaveta no
 * `<body>`, onde `fixed` volta a significar viewport.
 *
 * ## Por que `document` aqui é seguro
 * O `return null` vem ANTES do portal: durante o pré-render o menu está
 * fechado, a função sai sem tocar em `document`, e o portal só passa a
 * existir depois de um toque — que por definição acontece no navegador, com
 * a árvore já hidratada.
 */
export function MenuMobile({ aberto, ativo, onFechar }: MenuMobileProps) {
  /*
    Trava a rolagem do fundo enquanto a gaveta está aberta.

    Sem isto o dedo arrasta a página ATRÁS do menu, e ao fechar a pessoa está
    num lugar que não escolheu — o incômodo clássico de drawer em celular.
  */
  useEffect(() => {
    if (!aberto) return undefined;
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = anterior;
    };
  }, [aberto]);

  /* `Esc` fecha: a gaveta cobre a tela, e por teclado também se navega aqui. */
  useEffect(() => {
    if (!aberto) return undefined;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => {
      window.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto, onFechar]);

  if (!aberto) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 lg:hidden">
      <button
        type="button"
        aria-label="Fechar menu"
        onClick={onFechar}
        className="bg-auth-fundo-1/80 absolute inset-0"
      />
      <div className="border-auth-borda/10 bg-auth-fundo-2 absolute inset-y-0 right-0 flex w-72 max-w-[85vw] flex-col gap-8 border-l p-6">
        <div className="flex items-center justify-between">
          <span className="text-auth-texto text-sm font-medium tracking-tight">Menu</span>
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar menu"
            className="text-auth-texto-secundario hover:text-auth-texto"
          >
            <X size={20} aria-hidden />
          </button>
        </div>
        <NavLinks
          itens={ITENS_NAV}
          ativo={ativo}
          aoClicarLink={onFechar}
          className="flex-col gap-5 text-base"
        />
        <a
          href={URL_APP}
          className="bg-auth-marca-forte text-auth-marca-contraste rounded-auth-controle mt-auto px-4 py-3 text-center text-sm font-medium"
        >
          Entrar
        </a>
      </div>
    </div>,
    document.body,
  );
}
